import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const Stars = ({ value, onChange, size = 18 }) => (
  <div className="flex gap-1" data-testid="review-rating-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`star-${n}`}
        onClick={() => onChange && onChange(n)}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        aria-label={`${n} star`}
      >
        <Star size={size} className={n <= value ? "fill-electric text-electric" : "text-neutral-300"} />
      </button>
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });

  const load = async () => {
    try {
      const res = await api.get("/reviews");
      setReviews(res.data.reviews || []);
    } catch {
      toast.error("Could not load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.text.trim().length < 5) {
      toast.error("Please add your name and a short review");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you — your review is live");
      setForm({ name: "", rating: 5, text: "" });
      load();
    } catch {
      toast.error("Could not submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="bg-gradient-to-b from-mist to-[#dceaff] py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-electric mb-6">Patient Voices</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-ink leading-[0.95] mb-10">
              Reviews from<br />our patients.
            </h2>

            <form onSubmit={submit} className="bg-white rounded-lg border border-black/10 p-8 shadow-[0_8px_32px_rgba(0,26,77,0.06)]" data-testid="review-form">
              <h3 className="font-display font-bold text-xl text-ink mb-6">Leave a review</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Your name</label>
                  <Input
                    data-testid="review-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Sarah van Wyk"
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Rating</label>
                  <Stars value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} size={26} />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Your experience</label>
                  <Textarea
                    data-testid="review-text-input"
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    placeholder="Tell us about your treatment and recovery..."
                    rows={4}
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="review-submit-button"
                  disabled={submitting}
                  className="w-full h-12 rounded-full bg-navy hover:bg-electric text-white text-xs font-bold tracking-[0.2em] uppercase transition-colors duration-300"
                >
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : "Submit Review"}
                </Button>
              </div>
            </form>
          </motion.div>

          <div className="lg:col-span-7">
            {loading ? (
              <div className="flex items-center justify-center h-64" data-testid="reviews-loading">
                <Loader2 className="animate-spin text-electric" size={28} />
              </div>
            ) : reviews.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex flex-col items-start justify-center h-full min-h-[320px] rounded-lg border-2 border-dashed border-black/15 p-12"
                data-testid="reviews-empty-state"
              >
                <Quote size={40} className="text-electric mb-6" />
                <h3 className="font-display font-bold text-2xl text-ink mb-3">No reviews yet</h3>
                <p className="text-neutral-500 text-base max-w-sm leading-relaxed">
                  We're a brand-new practice in Plumstead. Be our very first patient story —
                  your feedback shapes how we care.
                </p>
              </motion.div>
            ) : (
              <div className="space-y-4" data-testid="reviews-list">
                {reviews.map((r, i) => (
                  <motion.article
                    key={r.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06, duration: 0.6 }}
                    className="bg-white rounded-lg border border-black/10 p-8"
                    data-testid={`review-card-${i}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <Stars value={r.rating} />
                      <span className="text-xs text-neutral-400">
                        {new Date(r.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                    <p className="text-neutral-600 text-base leading-relaxed mb-4">"{r.text}"</p>
                    <p className="font-display font-bold text-ink text-sm">{r.name}</p>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
